import { useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

const HOME_PATHS = ["/", "/home"];
const EXIT_CONFIRM_MS = 2000;

function isInstalledMobileApp() {
  if (typeof window === "undefined") return false;
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true;
  const mobile = window.innerWidth <= 768;
  return standalone || mobile;
}

function isHomePath(pathname) {
  return HOME_PATHS.includes(pathname);
}

export default function MobileBackHandler({
  isCreatePostOpen = false,
  onCloseCreatePost,
  isMessengerOpen = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state);
  const lastBackPressRef = useRef(0);
  const allowExitRef = useRef(false);

  const pushHistoryGuard = useCallback(() => {
    try {
      window.history.pushState(
        { ...(window.history.state || {}), mobileBackGuard: true },
        "",
        window.location.href
      );
    } catch (e) {
      // ignore
    }
  }, []);

  const handleBackAction = useCallback(() => {
    if (!user?.token) {
      return false;
    }

    if (isCreatePostOpen) {
      onCloseCreatePost?.();
      return true;
    }

    if (
      isMessengerOpen ||
      window.__messengerSidebarOpen ||
      window.__messengerChatOpen
    ) {
      window.dispatchEvent(new CustomEvent("messengerHardwareBack"));
      return true;
    }

    const historyIdx =
      typeof window.history.state?.idx === "number" ? window.history.state.idx : 0;

    if (!isHomePath(location.pathname) && historyIdx > 0) {
      navigate(-1);
      return true;
    }

    if (!isHomePath(location.pathname)) {
      navigate("/");
      return true;
    }

    const now = Date.now();
    if (now - lastBackPressRef.current < EXIT_CONFIRM_MS) {
      allowExitRef.current = true;
      lastBackPressRef.current = 0;
      return false;
    }

    lastBackPressRef.current = now;
    return true;
  }, [
    user?.token,
    isCreatePostOpen,
    onCloseCreatePost,
    isMessengerOpen,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    if (!isInstalledMobileApp() || !user?.token) {
      return undefined;
    }

    pushHistoryGuard();

    const onPopState = () => {
      if (allowExitRef.current) {
        allowExitRef.current = false;
        return;
      }

      const handled = handleBackAction();
      if (handled) {
        pushHistoryGuard();
      }
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [user?.token, handleBackAction, pushHistoryGuard]);

  useEffect(() => {
    if (!isInstalledMobileApp() || !user?.token) {
      return;
    }
    pushHistoryGuard();
    allowExitRef.current = false;
    lastBackPressRef.current = 0;
  }, [location.pathname, user?.token, pushHistoryGuard]);

  return null;
}
